import {Tr, TrSpace} from "@/i18n/i18n";
import {Keys} from "@/constants/key";
import {Creature, Force, WarriorClass} from "@/store/level/types";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {CreatureUtils} from "@/utils/creature-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "../utils/c-u";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import Image = Phaser.GameObjects.Image;

export class CreatureStatPanel extends Container {

    private readonly CANVAS_WIDTH = CU.CANVAS_WIDTH;
    private statWindow!: Image;
    private creature!: Creature | null;

    private statContainer!: Container;

    constructor(scene: Scene) {
        super(scene);

        this.scene = scene;
        this.setVisible(false);
        this.setDepth(5000);

    }

    public show(creature: Creature, x: number, y: number, flipY: boolean) {

        if (this.statContainer) { this.statContainer.destroy(); }

        if (creature.forcesV2) {
            if (creature.forcesV2.length > 7) {
                y += 22;
            } else if (creature.forcesV2.length > 4) {
                y += 11;
            }
        }

        this.y = y;
        this.x = x;

        this.prepareStatWindowContainer(creature, -65, x, y, flipY);
        this.add(this.statContainer);

        this.setVisible(true);

    }

    public destroyWindowCompletely() {
        if (this.statContainer && this.statContainer.active) {
            this.statContainer.destroy();
        }
        this.creature = null;
    }

    public isWindowDestroyed() {
        return !this.statContainer || !this.statContainer.active;
    }

    private prepareStatWindowContainer(creature: Creature, textY: number, pointerX: number, pointerY: number, flipY: boolean) {

        this.statContainer = new Container(this.scene, 0, 0);

        let scale = 1;
        if (creature.forcesV2) {
            if (creature.forcesV2.length > 7) {
                textY -= 30;
                scale = 1.3;
            } else if (creature.forcesV2.length > 4) {
                textY -= 20;
                scale = 1.2;
            } else {
                textY -= 10;
                scale = 1.1;
            }
        }

        this.statWindow = SpriteUtils.addSprite(this.scene, 0, 0, Keys.ATLAS_UI, Keys.UI_STAT_WINDOW, 1, scale)
            .setInteractive();

        let statWindowCorner = SpriteUtils.addSprite(this.scene, 2.5, this.statWindow.displayHeight / CU.S / 2 - 3.5, Keys.ATLAS_UI, Keys.UI_STAT_WINDOW_CORNER)
            .setInteractive();

        this.statContainer.add([this.statWindow, statWindowCorner]);

        if (flipY) {
            statWindowCorner.setFlipY(flipY);
            statWindowCorner.y = -(this.statWindow.displayHeight / 2 - 5 * CU.S);
        }

        if (pointerX - this.statWindow.displayWidth / 2 <= 0) {
            let diff = Math.abs(pointerX - this.statWindow.displayWidth / 2);
            this.x += diff;
            statWindowCorner.x -= diff;
        }

        if (pointerX + this.statWindow.displayWidth / 2 >= this.CANVAS_WIDTH) {
            let diff = Math.abs(this.CANVAS_WIDTH - (pointerX + this.statWindow.displayWidth / 2))
            this.x -= diff;
            statWindowCorner.x += diff;
        }

        if (pointerY + this.statWindow.displayHeight / 5 >= CU.CANVAS_HEIGHT / 2) {
            this.y -= this.statWindow.displayHeight + 65 * CU.S;
            statWindowCorner.setFlipY(false);
            statWindowCorner.y = this.statWindow.displayHeight / 2 - 3.5 * CU.S;
        }

        if (creature.name) {
            let xOffset = -160;

            let creatureNameText = creature.name ? creature.name.toUpperCase() : '';
            let creatureName = TextUtils.addTextAdvanced(this.scene, creatureNameText, xOffset, textY,
                '#eec761', 15, 'normal', 250, 'left', 2, Fonts.BENGALY_NORMAL);


            let lev = creature.actualStair > 0 ? creature.actualStair : (creature.protoStair > 0 ? creature.protoStair : 0);
            let levTxt = lev > 0 ? (TrSpace('ur.large.dot') + lev + ', ') : '';
            levTxt += (Tr('klass') + CreatureUtils.getClassName(creature.warriorClass));
            levTxt += (creature.level > 1 ? (' (' + creature.level + ')') : '');

            let creatureType = TextUtils.addTextAdvanced(this.scene, levTxt, xOffset, creatureName.y / CU.S + 20,
                '#b1b0b0', 12, 'normal', 300, 'left', 2, Fonts.MYRIADPRO_REGULAR);

            let separator = SpriteUtils.addSprite(this.scene, this.statWindow.x, creatureType.getBottomCenter().y! / CU.S + 5, Keys.ATLAS_UI, Keys.UI_STAT_WINDOW_SEPARATOR);
            separator.displayWidth = this.statWindow.displayWidth - 35;

            let rang = creature.level > 1 ? (' (' + creature.level + ')') : ':';
            let classTitle = TextUtils.addTextAdvanced(this.scene, Tr('cl.ftrs') + rang,
                xOffset, separator.getBottomCenter().y! / CU.S + 12,
                '#e5e5e5', 11, 'normal', 350, 'left', 2, Fonts.MYRIADPRO_REGULAR);

            this.statContainer.add([classTitle, creatureName, creatureType, separator]);

            this.prepareCreatureFeatures(xOffset, classTitle.y / CU.S + 12, creature, creature.warriorClass, this.statWindow.displayWidth / CU.S - 35);
        }
    }

    private prepareCreatureFeatures(x: number, y: number, c: Creature, type: WarriorClass, lineWidth: number) {
        const fontSize = 11;
        const colorPros = '#8bff80';
        const colorCons = '#ff9999';

        let descStrings = c.rankDescription ? c.rankDescription.toUpperCase().split('\n') : [''];

        let texts: Phaser.GameObjects.Text[] = [];
        for (let i = 0; i < descStrings.length; i++) {
            if(descStrings[i].trim() == '') continue;
            if(type == WarriorClass.GIN && c.level == 1 && i == descStrings.length - 1){
                texts.push(this.generateText(x, i > 0? texts[texts.length -1].getBottomCenter().y!/CU.S + 0 : y, '- ' + descStrings[i], colorCons, fontSize, lineWidth));
                return;
            }

            texts.push(this.generateText(x, i > 0? texts[texts.length -1].getBottomCenter().y!/CU.S + 0 : y, '- ' + descStrings[i], colorPros, fontSize, lineWidth));
        }

        this.statContainer.add([
            ...texts
        ]);

        let forcesTxt = '';
        if (c.forcesV2 && c.forcesV2.length > 0) {
            if (c.forcesV2.length === 1) {
                forcesTxt = TrSpace('force') + this.getForceName(c.forcesV2[0]);
            } else {
                forcesTxt = Tr('forces') + ': ';
                c.forcesV2.forEach(f => {
                    forcesTxt += this.getForceName(f) +  ', ';
                });
                forcesTxt = forcesTxt.substr(0, forcesTxt.length - 2);
            }
        }

        let forces = TextUtils.addTextAdvanced(this.scene, forcesTxt,
            x, this.bottomY + 2,
            '#8effe9', 11, 'normal', 335, 'left', 2, Fonts.MYRIADPRO_BOLD).setOrigin(0, 0);
        this.statContainer.add(forces);

    }
    private getForceName(f:Force){
        return CreatureUtils.getClassNameGenetive(f.warriorClass) + (f.level > 1? ' (' + f.level + ')' : '')
    }

    private bottomY: number = 0;

    private generateText(x: number, y: number, text: string, color: string, fontSize: number, lineWidth: number) {
        let res = TextUtils.addTextAdvanced(this.scene, text, x, y, color, fontSize,
            'normal', lineWidth, 'left', 0, Fonts.MYRIADPRO_REGULAR)
            .setOrigin(0, 0);
        this.bottomY = res.getBottomCenter().y! / CU.S;
        return res;
    }

}
